import React, { useState } from 'react';
import { signUpWithEmail, signInWithEmail, signInWithGoogle } from '../../services/authService';
import { isFirebaseAvailable, testFirebaseConnection } from '../../services/firebase';
import toast from 'react-hot-toast';

const AuthTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: ''
  });

  const addResult = (test, status, message) => {
    setTestResults(prev => [...prev, { test, status, message, timestamp: new Date() }]);
  };

  const runFirebaseTests = async () => {
    setLoading(true);
    setTestResults([]);

    try {
      // Test 1: Firebase Availability
      addResult('Firebase Availability', 'testing', 'Checking if Firebase is initialized...');
      const available = isFirebaseAvailable();
      addResult('Firebase Availability', available ? 'success' : 'error', 
        available ? 'Firebase is properly initialized' : 'Firebase is not initialized');

      // Test 2: Firebase Connection
      addResult('Firebase Connection', 'testing', 'Testing Firebase connection...');
      const connected = await testFirebaseConnection();
      addResult('Firebase Connection', connected ? 'success' : 'error',
        connected ? 'Firebase connection successful' : 'Firebase connection failed');

      // Test 3: Authentication Service
      addResult('Auth Service', 'testing', 'Testing authentication service...');
      try {
        // Try to access auth object
        const { auth } = await import('../../services/firebase');
        addResult('Auth Service', auth ? 'success' : 'error',
          auth ? 'Authentication service is available' : 'Authentication service is not available');
      } catch (error) {
        addResult('Auth Service', 'error', `Auth service error: ${error.message}`);
      }

    } catch (error) {
      addResult('General Test', 'error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testAccountCreation = async () => {
    if (!formData.email || !formData.password || !formData.displayName) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    addResult('Account Creation', 'testing', `Attempting to create account for ${formData.email}...`);

    try {
      const result = await signUpWithEmail(formData.email, formData.password, formData.displayName);
      
      if (result.success) {
        addResult('Account Creation', 'success', 'Account created successfully!');
        toast.success('Account created successfully!');
      } else {
        addResult('Account Creation', 'error', `Account creation failed: ${result.error}`);
        toast.error(`Account creation failed: ${result.error}`);
      }
    } catch (error) {
      addResult('Account Creation', 'error', `Unexpected error: ${error.message}`);
      toast.error(`Unexpected error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testGoogleSignIn = async () => {
    setLoading(true);
    addResult('Google Sign In', 'testing', 'Attempting Google sign in...');

    try {
      const result = await signInWithGoogle();
      
      if (result.success) {
        addResult('Google Sign In', 'success', 'Google sign in successful!');
        toast.success('Google sign in successful!');
      } else {
        addResult('Google Sign In', 'error', `Google sign in failed: ${result.error}`);
        toast.error(`Google sign in failed: ${result.error}`);
      }
    } catch (error) {
      addResult('Google Sign In', 'error', `Unexpected error: ${error.message}`);
      toast.error(`Unexpected error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-2xl font-bold mb-6">Authentication Debug Tool</h1>
      
      {/* Firebase Tests */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Firebase Tests</h2>
        <button
          onClick={runFirebaseTests}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {loading ? 'Running Tests...' : 'Run Firebase Tests'}
        </button>
      </div>

      {/* Account Creation Test */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Account Creation Test</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            name="displayName"
            value={formData.displayName}
            onChange={handleInputChange}
            placeholder="Display Name"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Password"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex space-x-4">
          <button
            onClick={testAccountCreation}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : 'Test Account Creation'}
          </button>
          <button
            onClick={testGoogleSignIn}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {loading ? 'Signing In...' : 'Test Google Sign In'}
          </button>
        </div>
      </div>

      {/* Test Results */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Test Results</h2>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {testResults.map((result, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg border-l-4 ${
                result.status === 'success'
                  ? 'bg-green-50 border-green-500 text-green-800'
                  : result.status === 'error'
                  ? 'bg-red-50 border-red-500 text-red-800'
                  : 'bg-yellow-50 border-yellow-500 text-yellow-800'
              }`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{result.test}</div>
                  <div className="text-sm">{result.message}</div>
                </div>
                <div className="text-xs text-gray-500">
                  {result.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold text-blue-800 mb-2">Instructions:</h3>
        <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
          <li>First, run the Firebase tests to ensure everything is properly configured</li>
          <li>Fill in the form fields and test account creation</li>
          <li>Check the browser console for detailed logs</li>
          <li>Review the test results below for any errors</li>
        </ol>
      </div>
    </div>
  );
};

export default AuthTest; 